import React, { useRef, useState } from 'react'
import { Textarea } from '~/lib/ui/textarea'
import { Label } from '~/lib/ui/label'
import { Button } from '~/lib/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from '~/lib/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { useCoachDashboard } from './CoachDashboardProvider'
import PrivacySettings from '../CoachCandidateView/PrivacySettings'
import { apiPost } from '~/util/api'
import { useToast } from '~/lib/ui/toast'
import Quill from 'quill'
import RichTextEditor from '~/lib/ui/rich-text-editor'
export const DurationSelect = ({ duration, onValueChange }) => {
  const durationOptions = [1, 2, 5, 10, 15, 30, 45, 60, 75, 90, 'Custom']
  const [hasCustomDuration, setHasCustomDuration] = useState(false)
  const handleValueChange = (value) => {
    if (value === 'Custom') {
      setHasCustomDuration(true)
    } else {
      setHasCustomDuration(false)
      onValueChange(value)
    }
  }
  return (
    <div className="flex flex-row justify-evenly">
      <Select
        defaultValue={duration?.toString()}
        onValueChange={handleValueChange}
        className="w-full"
      >
        <SelectTrigger>
          <SelectValue placeholder="Select Duration" />
        </SelectTrigger>
        <SelectContent>
          {durationOptions.map((option) => (
            <SelectItem key={option} value={option.toString()}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasCustomDuration && (
        <input
          type="number"
          className="ml-2 w-full"
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Custom Duration"
        />
      )}
    </div>
  )
}

const NoShowDialog = ({ calendlyEvent, children }) => {
  const { setDashboardData, current_user, setUnfilteredDashboardData } =
    useCoachDashboard()
  const { id: coachId, first_name: coachName } = current_user
  const { candidateId, candidateFirstName } = calendlyEvent

  const [open, setOpen] = useState(false)
  const [noteDetails, setNoteDetails] = useState('')
  const [privacySettings, setPrivacySettings] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const editorRef = useRef(null)
  const allPrivacySettings = ['csm', 'coach', 'admin']
  const formPrivacySettings =
    privacySettings.indexOf('all') > -1 ? allPrivacySettings : privacySettings
  const [duration, setDuration] = useState()
  const { toast } = useToast()

  const prefix = candidateFirstName ? `Hi ${candidateFirstName},\n\n` : ''
  const suffix = coachName
    ? `\n\nLooking forward to catching up soon,\n${coachName}`
    : ''

  const defaultMessage = {
    ops: [
      {
        insert:
          prefix +
          'I noticed we weren’t able to connect for our meeting today. I completely understand that things come up unexpectedly, and I want to make sure you’re supported as you move forward.\n\nIf something prevented you from joining, just let me know — I’m happy to help or adjust things for next time. Going forward, if you need to cancel, I’d appreciate a 24-hour notice so we can make sure your time and support are protected.\n\nWhenever you’re ready, let’s find a time that works for you to reschedule.\n\n{reschedule_button} or {send_me_a_message_button}' +
          suffix,
      },
    ],
  }

  const quillTemp = document.createElement('div')
  const quillInstance = new Quill(quillTemp)
  quillInstance.setContents(defaultMessage)

  const noteToCandidate = editorRef?.current?.getSemanticHTML()

  const noShowError = (error) => {
    setIsSaving(false)
    const description =
      error?.response?.data?.error ||
      'An error occurred while uploading the no-show entry.'
    console.log('Error description:', description)
    toast({
      description,
      title: 'Could Not Upload No-Show Entry',
      variant: 'destructive',
      duration: 10_000,
    })
  }

  const handleNoShow = async (formData) => {
    setIsSaving(true)
    try {
      const response = await apiPost(
        `/coach/candidates/${candidateId}/service_request_no_shows.json?from=coach_dashboard&message=${encodeURIComponent(noteToCandidate)}`,
        formData,
        true,
      )
      if (response.status === 200) {
        const calendlyEvent = response.data.meeting
        setDashboardData((prevData) => ({
          ...prevData,
          meetings: prevData.meetings.filter(
            (meeting) => meeting.id !== calendlyEvent.id,
          ),
        }))
        setUnfilteredDashboardData((prevData) => ({
          ...prevData,
          meetings: prevData.meetings.filter(
            (meeting) => meeting.id !== calendlyEvent.id,
          ),
        }))
        setIsSaving(false)
        setOpen(false)
        toast({
          title: 'Success',
          description: 'No-show entry created successfully',
        })
      } else {
        setIsSaving(false)
        const description =
          'An error occurred while uploading the no-show entry.'
        noShowError(new Error(description))
      }
    } catch (error) {
      console.error('Error handling no-show:', error)
      noShowError(error)
    }
  }

  const saveClick = async () => {
    if (isSaving) return null

    const f = new FormData()
    f.append('service_request_no_show[coach_id]', coachId)
    f.append('service_request_no_show[candidate_id]', candidateId)
    f.append('service_request_no_show[calendly_event_id]', calendlyEvent.id)
    f.append(
      'service_request_no_show[coach_note_attributes][duration]',
      duration,
    )
    f.append(
      'service_request_no_show[coach_note_attributes][topics][Resume][]',
      'No Show',
    )
    formPrivacySettings.forEach((setting) => {
      f.append(
        'service_request_no_show[coach_note_attributes][privacy_settings][]',
        setting,
      )
    })
    f.append(
      'service_request_no_show[coach_note_attributes][note]',
      noteDetails,
    )
    handleNoShow(f)
  }

  const resetForm = () => {
    setNoteDetails('')
    setPrivacySettings([])
    setIsSaving(false)
    setDuration('30')
  }

  const handleDialogOpenChange = (isOpen) => {
    setOpen(isOpen)
    if (!isOpen) {
      resetForm()
    }
  }

  const noDuration =
    duration === '' || duration === undefined || duration.length < 1

  const formIsInvalid =
    noteToCandidate?.toUpperCase().includes('COACH TO EDIT') ||
    noteToCandidate?.trim() === undefined ||
    noteToCandidate?.trim() === '' ||
    noDuration

  return (
    <Dialog
      open={open}
      onOpenChange={handleDialogOpenChange}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          variant="link"
          className="m-0 justify-start p-0 font-normal text-intoo-blue-medium hover:text-link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2 className="text-2xl">New No-Show Entry</h2>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[80vh] overflow-y-auto px-4 pb-4">
          <DialogDescription className="p-0 text-lg">
            Mark this candidate as a no-show for the scheduled meeting.
          </DialogDescription>
          <div className="py-4">
            <Label htmlFor="duration" className="text-lg !font-medium">
              Duration (Minutes) (Required)
            </Label>
            <DurationSelect duration={duration} onValueChange={setDuration} />
          </div>
          <div className="pb-4">
            <Label htmlFor="noteDetails" className="text-lg !font-medium">
              Internal System Admin Notes
            </Label>
            <PrivacySettings
              setPrivacySettings={setPrivacySettings}
              privacySettings={privacySettings}
            />
            <Textarea
              className="min-h-56 w-full"
              name="noteDetails"
              value={noteDetails}
              onChange={(e) => setNoteDetails(e.target.value)}
            />
          </div>
          <hr className="border-t-2 border-gray-300" />
          <div className="py-4">
            <Label htmlFor="noteToCandidate" className="text-lg !font-medium">
              Please record any remarks with this update. Comments will be
              viewed by the candidate.
            </Label>
            <RichTextEditor
              className="min-h-56 w-full"
              defaultValue={defaultMessage}
              ref={editorRef}
            />
          </div>
          <div className="flex flex-row justify-end">
            <Button
              className="inline"
              onClick={saveClick}
              disabled={isSaving || formIsInvalid}
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default NoShowDialog
